// Shared hover affordance for line handles (endpoint handles + the
// middle-segment handle). On hover the handle fills with the LINE's own colour
// and the pointer becomes a move cursor, signalling "grab here to drag". Both
// revert on leave. Kept in one place so every line handle behaves identically.

import type Konva from 'konva';
import { HANDLE_FILL } from '../../constants/whiteboard-config.constants.js';

/**
 * Wire mouseenter/mouseleave on a handle so it fills with the current line
 * colour and shows a move cursor while hovered. `getLineColor` is read live so
 * a colour change in the options bar is reflected on the next hover; it may
 * return `undefined` (handle falls back to the resting fill).
 *
 * Fill is only touched in the RESTING state — callers that drive a drag/snap
 * look (e.g. the endpoint handle's magnetic-snap highlight) own the fill while
 * the pointer holds the handle, and the pointer never leaves it mid-drag, so
 * the two never fight.
 */
export function wireHandleHover(
  handle: Konva.Shape,
  getLineColor: () => string | undefined,
): void {
  handle.on('mouseenter', () => {
    handle.fill(getLineColor() ?? HANDLE_FILL);
    const container = handle.getStage()?.container();
    if (container) container.style.cursor = 'move';
    handle.getLayer()?.batchDraw();
  });
  handle.on('mouseleave', () => {
    handle.fill(HANDLE_FILL);
    const container = handle.getStage()?.container();
    if (container) container.style.cursor = '';
    handle.getLayer()?.batchDraw();
  });
}
